// Velkra Distribution PWA — API client.
// All requests go to the backend at the same origin in production.
// In development with Vite on :5174, set VITE_API_BASE to the backend URL.

const API_BASE = import.meta.env.VITE_API_BASE || '';
// v160.4.93: also export so consumers (WebSocket subscribers, etc.) can
// derive the WS URL by replacing http→ws.
export { API_BASE };

const TOKEN_KEY = 'velkra_dist_token';
const TESTER_KEY = 'velkra_dist_tester';

export function getToken() {
  return localStorage.getItem(TOKEN_KEY);
}

export function setAuth(token, tester) {
  localStorage.setItem(TOKEN_KEY, token);
  localStorage.setItem(TESTER_KEY, JSON.stringify(tester));
  // Cache token for the service worker pushsubscriptionchange handler.
  if ('caches' in window) {
    caches.open('velkra-dist-auth').then(c => c.put('/auth-token', new Response(token)));
  }
}

export function clearAuth() {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(TESTER_KEY);
  // v160.3.1: also clear the SW cache so pushsubscriptionchange doesn't
  // re-use a dead token.
  if ('caches' in window) {
    caches.open('velkra-dist-auth').then(c => c.delete('/auth-token')).catch(() => {});
  }
  // Best-effort tell the backend we're done so it can drop the session.
  const t = localStorage.getItem(TOKEN_KEY);
  if (t) {
    fetch((import.meta.env.VITE_API_BASE || '') + '/v1/dist/me/signout', {
      method: 'POST', headers: { Authorization: `Bearer ${t}` }
    }).catch(() => {});
  }
}

export function getTester() {
  try { return JSON.parse(localStorage.getItem(TESTER_KEY) || 'null'); }
  catch { return null; }
}

async function request(method, path, body, opts = {}) {
  const headers = { ...(opts.headers || {}) };
  if (body !== undefined && !(body instanceof FormData)) {
    headers['Content-Type'] = 'application/json';
  }
  const token = getToken();
  if (token && !opts.skipAuth) headers['Authorization'] = `Bearer ${token}`;

  const res = await fetch(API_BASE + path, {
    method,
    headers,
    body: body === undefined ? undefined : (body instanceof FormData ? body : JSON.stringify(body)),
    credentials: opts.credentials || 'omit'
  });

  if (res.status === 401 && !opts.skipAuth) {
    clearAuth();
    if (window.location.pathname !== '/login') window.location.href = '/login';
    throw new Error('Unauthorized');
  }
  if (!res.ok) {
    // v160.4.23: clone the response before the first read so we can fall
    // back to text() if json() fails. Without the clone, the body is
    // already "disturbed or locked" by the failed json() call and the
    // catch path crashes with that error instead of surfacing the real
    // backend error.
    const cloneForText = res.clone();
    let detail = '';
    let parsedJson = null;
    try {
      parsedJson = await res.json();
      detail = parsedJson.error || JSON.stringify(parsedJson);
    } catch {
      try { detail = await cloneForText.text(); } catch { detail = '(unreadable response body)'; }
    }
    // v160.4.99f: attach status + parsed JSON to the Error so callers can
    // surface field-level detail (e.g. backend 409 returns `{error, detail}`
    // and the UI wants to show both lines, not just the headline `error`).
    // Pre-fix, callers had to regex `e.message` to get the status code, and
    // any structured fields beyond `error` were lost.
    const err = new Error(`${res.status}: ${detail.slice(0, 500)}`);
    err.status = res.status;
    if (parsedJson) {
      err.body = parsedJson;
      if (parsedJson.error) err.error = parsedJson.error;
      if (parsedJson.detail) err.detail = parsedJson.detail;
    }
    throw err;
  }
  const ct = res.headers.get('content-type') || '';
  if (ct.includes('application/json')) return res.json();
  if (ct.includes('text/')) return res.text();
  return res.blob();
}

export const api = {
  get: (p, o) => request('GET', p, undefined, o),
  post: (p, b, o) => request('POST', p, b, o),
  patch: (p, b, o) => request('PATCH', p, b, o),
  put: (p, b, o) => request('PUT', p, b, o),
  delete: (p, o) => request('DELETE', p, undefined, o),
};

// ── Auth ─────────────────────────────────────────────────────────────────
export async function loginWithApple(idToken, user) {
  const result = await api.post('/v1/dist/auth/apple', { id_token: idToken, user }, { skipAuth: true });
  setAuth(result.token, result.tester);
  return result;
}

// ── Tester self ──────────────────────────────────────────────────────────
export const me = {
  fetch: () => api.get('/v1/dist/me'),
  apps: () => api.get('/v1/dist/me/apps'),
  builds: (slug) => api.get(`/v1/dist/me/apps/${slug}/builds`),
  feedback: () => api.get('/v1/dist/me/feedback'),
  // v160.4.99f: tester-side hard delete. Rows in 'requested' or
  // 'in_progress' fix states return 409 — caller should show the detail.
  deleteFeedback: (id) => api.delete(`/v1/dist/me/feedback/${id}`),
};

// ── Devices ──────────────────────────────────────────────────────────────
export const devices = {
  startRegistration: () => api.post('/v1/dist/devices/start', {}),
  // v160.3.3: Manual UDID paste — for testers who got their UDID from
  // iTunes/Finder/3uTools/get.udid.io and want to skip the .mobileconfig flow.
  registerManual: (udid, deviceName, deviceClass) =>
    api.post('/v1/dist/devices/manual', { udid, deviceName, deviceClass }),
};

// ── Builds (install URLs are public) ─────────────────────────────────────
export function installURL(buildId) {
  return `${API_BASE}/v1/dist/install/${buildId}`;
}
export function itmsServicesURL(buildId) {
  const host = API_BASE || window.location.origin;
  return `itms-services://?action=download-manifest&url=${encodeURIComponent(host + '/v1/dist/builds/' + buildId + '/manifest.plist')}`;
}
// v160.4.87: install-status polling. Public endpoint (no auth) — returns
// { manifestFetched: bool } based on whether iOS has fetched the manifest
// for this build in the last 10 minutes. Used by AppDetail to give live
// install feedback after the tester taps "Install on this iPhone".
export function installStatusURL(buildId) {
  return `${API_BASE}/v1/dist/builds/${buildId}/install-status`;
}

// ── Push subscription ────────────────────────────────────────────────────
export const push = {
  vapidKey: () => api.get('/v1/dist/push/vapid-public-key', { skipAuth: true }),
  subscribe: (endpoint, keys) => api.post('/v1/dist/push/subscribe', { endpoint, keys }),
  // v160.4.58: pass endpoint as a query param. The previous version did
  // `api.delete(path, { headers: ... })` which threw `endpoint` away — the
  // DELETE handler then 400'd on empty body, leaving orphan rows in
  // dist_push_subs. Backend now reads ?endpoint= as an alternative to body.
  unsubscribe: (endpoint) =>
    api.delete('/v1/dist/push/subscribe?endpoint=' + encodeURIComponent(endpoint)),
};

// ── Admin ────────────────────────────────────────────────────────────────
export const admin = {
  testers: {
    list: () => api.get('/v1/dist/admin/testers'),
    create: (b) => api.post('/v1/dist/admin/testers', b),
    remove: (id) => api.delete(`/v1/dist/admin/testers/${id}`),
  },
  groups: {
    list: () => api.get('/v1/dist/admin/groups'),
    create: (b) => api.post('/v1/dist/admin/groups', b),
    addMembers: (id, testerIds) => api.post(`/v1/dist/admin/groups/${id}/members`, { testerIds }),
  },
  devices: {
    list: () => api.get('/v1/dist/admin/devices'),
    sync: (id) => api.post(`/v1/dist/admin/devices/${id}/sync-asc`, {}),
    syncAll: () => api.post('/v1/dist/admin/asc/sync-all', {}),
    exportCSV: () => `${API_BASE}/v1/dist/admin/devices/export.csv`,
  },
  builds: {
    list: (filters = {}) => {
      const qs = new URLSearchParams(filters).toString();
      return api.get('/v1/dist/admin/builds' + (qs ? '?' + qs : ''));
    },
    update: (id, b) => api.patch(`/v1/dist/admin/builds/${id}`, b),
    setTargets: (id, groupIds) => api.post(`/v1/dist/admin/builds/${id}/targets`, { groupIds }),
    remove: (id) => api.delete(`/v1/dist/admin/builds/${id}`),
    // v160.3.2: TestFlight promotion removed — Distribution Hub is for
    // ad-hoc distribution only. TF uploads happen manually via Xcode.
    testPlan: (id) => api.post(`/v1/dist/admin/builds/${id}/test-plan`, {}),
    expiryWarnings: () => api.get('/v1/dist/admin/expiry-warnings'),
  },
  feedback: {
    list: (filters = {}) => {
      const qs = new URLSearchParams(filters).toString();
      return api.get('/v1/dist/admin/feedback' + (qs ? '?' + qs : ''));
    },
    update: (id, b) => api.patch(`/v1/dist/admin/feedback/${id}`, b),
    screenshotURL: (id) => `${API_BASE}/v1/dist/admin/feedback/${id}/screenshot`,
  },
  crashes: {
    list: (filters = {}) => {
      const qs = new URLSearchParams(filters).toString();
      return api.get('/v1/dist/admin/crashes' + (qs ? '?' + qs : ''));
    },
  },
  abTest: {
    create: (b) => api.post('/v1/dist/admin/ab-test', b),
    results: (abId) => api.get(`/v1/dist/admin/ab-test/${abId}`),
  },
  rollback: {
    set: (slug, b) => api.put(`/v1/dist/admin/rollback-config/${slug}`, b),
  },
  shareLinks: {
    create: (buildId, expiresInHours, maxInstalls) =>
      api.post('/v1/dist/admin/share-links', { buildId, expiresInHours, maxInstalls }),
  },
  storage: {
    usage: () => api.get('/v1/dist/admin/storage'),
    sweep: () => api.post('/v1/dist/admin/storage/sweep', {}),
  },
  webhooks: {
    set: (slug, b) => api.put(`/v1/dist/admin/webhooks/${slug}`, b),
    get: (slug) => api.get(`/v1/dist/admin/webhooks/${slug}`),
    test: (slug) => api.post(`/v1/dist/admin/webhooks/${slug}/test`, {}),
  },
  // v160.3.1
  audit: (filters = {}) => {
    const qs = new URLSearchParams(filters).toString();
    return api.get('/v1/dist/admin/audit' + (qs ? '?' + qs : ''));
  },
  reliability: () => api.get('/v1/dist/admin/testers/reliability'),
  comments: {
    list: (buildId) => api.get(`/v1/dist/builds/${buildId}/comments`),
    post: (buildId, body, parentId) => api.post(`/v1/dist/builds/${buildId}/comments`, { body, parentId }),
    delete: (id) => api.delete(`/v1/dist/admin/comments/${id}`),
  },
  compare: (idA, idB) => api.get(`/v1/dist/admin/builds/${idA}/compare/${idB}`),
  dsym: {
    upload: async (buildId, file) => {
      const token = getToken();
      const buf = await file.arrayBuffer();
      const r = await fetch((import.meta.env.VITE_API_BASE || '') + `/v1/dist/admin/builds/${buildId}/dsym`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/octet-stream', 'Authorization': `Bearer ${token}` },
        body: buf
      });
      if (!r.ok) throw new Error(`dSYM upload ${r.status}: ${(await r.text()).slice(0, 200)}`);
      return r.json();
    }
  },
  triage: {
    get: (feedbackId) => api.get(`/v1/dist/admin/feedback/${feedbackId}/triage`),
    rerun: (feedbackId) => api.post(`/v1/dist/admin/feedback/${feedbackId}/triage`, {}),
  },
  // v160.4.4: renamed from .testResults to avoid shadowing the tester-side
  // `testResults` export below. Admin reads aggregate results from all testers
  // on a build. Tester has separate endpoints under `testResults.mine/set`.
  buildTestResults: {
    forBuild: (buildId) => api.get(`/v1/dist/admin/builds/${buildId}/test-results`),
  },
  // v160.4.99x/y: auto-fix verification stats. autoFixSuccess returns
  // aggregate verified-pass/fail rates per app — answers "is auto-fix
  // Claude actually shipping fixes that work?" verifyLeaderboard ranks
  // testers by verification volume per app. autoPilotStats covers the
  // earlier v99w plan-quality telemetry. runVerifyReminders is the
  // admin-triggered cron entry point for the stale-verification push
  // sweep — call it from your scheduler every few hours.
  autoFixSuccess: (slug, sinceDays = 30) =>
    api.get(`/v1/dist/admin/apps/${slug}/auto-fix-success-rate?sinceDays=${sinceDays}`),
  verifyLeaderboard: (slug, limit = 10) =>
    api.get(`/v1/dist/admin/apps/${slug}/verification-leaderboard?limit=${limit}`),
  autoPilotStats: (buildId) =>
    api.get(`/v1/dist/admin/builds/${buildId}/auto-pilot-stats`),
  runVerifyReminders: () =>
    api.post('/v1/dist/admin/run-verification-reminders', {}),
  // v160.5.0: AI infra health. macHealth surfaces per-Mac success rates
  // (catches expired claude.ai login). aiActivityForApp powers the
  // "subscription Claude saved you $X" line on AppDetail.
  macHealth: (windowMinutes = 60) =>
    api.get(`/v1/dist/admin/ai-jobs/mac-health?windowMinutes=${windowMinutes}`),
  aiActivityForApp: (slug, sinceHours = 24) =>
    api.get(`/v1/dist/admin/apps/${slug}/ai-activity?sinceHours=${sinceHours}`),
  // v161.0: visual regression detection. Flagged when an auto-pilot
  // step's screenshot hash differs from the same step's prior-build
  // hash by more than the threshold (default 16/256 bits ~ 6%).
  visualRegressionsForBuild: (buildId, limit = 50) =>
    api.get(`/v1/dist/admin/builds/${buildId}/visual-regressions?limit=${limit}`),
  // v161.1: mark an intentional regression as expected — stops it from
  // re-flagging on every subsequent build until the design changes again.
  dismissVisualRegression: (visualId, reason = '') =>
    api.post(`/v1/dist/admin/visual-regressions/${visualId}/dismiss`, { reason }),
  undismissVisualRegression: (visualId) =>
    api.post(`/v1/dist/admin/visual-regressions/${visualId}/undismiss`, {}),
};

// v160.3.1: tester-side new APIs
export const handoff = {
  start: (appSlug = 'all') => api.post('/v1/dist/me/handoff/start', { appSlug }),
};
export const channels = {
  list: () => api.get('/v1/dist/me/channels'),
  subscribe: (appSlug, channel) => api.put(`/v1/dist/me/channels/${appSlug}/${channel}`, {}),
  unsubscribe: (appSlug, channel) => api.delete(`/v1/dist/me/channels/${appSlug}/${channel}`),
};
export const downgrade = {
  previousBuild: (slug) => api.get(`/v1/dist/me/apps/${slug}/previous-build`),
};
export const testResults = {
  mine: (buildId) => api.get(`/v1/dist/me/builds/${buildId}/test-results`),
  set: (buildId, itemIndex, passed, note) =>
    api.put(`/v1/dist/me/builds/${buildId}/test-results/${itemIndex}`, { passed, note }),
};

// v160.4.0: closed-loop auto-fix
export const autoFix = {
  // Tester
  request: (buildId) => api.post(`/v1/dist/me/builds/${buildId}/request-fix`, {}),
  myRequests: () => api.get('/v1/dist/me/fix-requests'),
  // Admin
  queue: () => api.get('/v1/dist/admin/fix-queue'),
  attempt: (id) => api.get(`/v1/dist/admin/fix-attempts/${id}`),
  approve: (id) => api.post(`/v1/dist/admin/fix-attempts/${id}/approve`, {}),
  reject: (id, reason) => api.post(`/v1/dist/admin/fix-attempts/${id}/reject`, { reason }),
  config: {
    get: (slug) => api.get(`/v1/dist/admin/auto-fix/config/${slug}`),
    set: (slug, body) => api.put(`/v1/dist/admin/auto-fix/config/${slug}`, body),
  },
};

// v160.4.93: tester-initiated fix request + live progress polling
export function fixProgress(requestId) {
  return api.get(`/v1/dist/me/fix-requests/${requestId}/progress`);
}
export function submitFixRequest({ feedbackIds, appSlug, buildId }) {
  return api.post('/v1/dist/me/feedback/submit-fix-request', { feedbackIds, appSlug, buildId });
}

// v160.4.93c: cancel a stuck fix request
export function cancelFixRequest(requestId) {
  return api.post(`/v1/dist/me/fix-requests/${requestId}/cancel`, {});
}


// v161.22.24: structured bug report intake
// Reports group multiple bug entries filed during one testing session.
// On submit, every entry becomes a dist_feedback row and the whole report
// is queued as a single dist_fix_requests run.
export const bugReports = {
  list:    () => api.get('/v1/dist/me/bug-reports'),
  get:     (id) => api.get(`/v1/dist/me/bug-reports/${id}`),
  create:  ({ appSlug, buildId, title, deviceModel, iosVersion }) =>
    api.post('/v1/dist/me/bug-reports',
      { appSlug, buildId, title, deviceModel, iosVersion }),
  update:  (id, fields) => api.patch(`/v1/dist/me/bug-reports/${id}`, fields),
  remove:  (id) => api.delete(`/v1/dist/me/bug-reports/${id}`),
  addEntry: (id, entry) =>
    api.post(`/v1/dist/me/bug-reports/${id}/entries`, entry),
  updateEntry: (reportId, entryId, fields) =>
    api.patch(`/v1/dist/me/bug-reports/${reportId}/entries/${entryId}`, fields),
  removeEntry: (reportId, entryId) =>
    api.delete(`/v1/dist/me/bug-reports/${reportId}/entries/${entryId}`),
  submit:  (id) => api.post(`/v1/dist/me/bug-reports/${id}/submit`, {}),
  // v161.22.27: tester-side archive (only valid for submitted/fixed reports).
  archive: (id) => api.post(`/v1/dist/me/bug-reports/${id}/archive`, {}),
  // v161.22.30: tester insists this report is NOT a duplicate of a
  // previously-fixed bug. Transitions from 'likely_duplicate' back to
  // 'submitted' and queues the fix request that was skipped at submit.
  overrideLikelyDuplicate: (id) =>
    api.post(`/v1/dist/me/bug-reports/${id}/override-likely-duplicate`, {}),
  // v161.22.31: chronological attempt history for the detail page. Shows
  // every fix run in the chain (including regression retries) with build
  // labels, diffs, and per-entry tester verdicts.
  attemptHistory: (id) =>
    api.get(`/v1/dist/me/bug-reports/${id}/attempt-history`),
  // v161.22.29: alternative submission — upload a PDF directly instead
  // of filling out the structured form. Creates a one-entry submitted
  // report and queues it for auto-fix. `pdfBase64` is the file content;
  // the rest match the structured create endpoint.
  uploadPdf: (payload) =>
    api.post('/v1/dist/me/bug-reports/upload-pdf', payload),
  // v161.22.25: poll the reply thread (Claude's status updates, summaries,
  // and any error notes inserted automatically during the auto-fix run).
  replies: (id) => api.get(`/v1/dist/me/bug-reports/${id}/replies`),
  // v161.22.26: per-entry verification. After a fix is distributed, the
  // tester marks each entry passed/failed. `passed=false` flips the entry
  // to 'regressed'; `passed=true` flips to 'fix_confirmed' and (if every
  // entry is confirmed) marks the report 'fixed'.
  verifyEntry: (reportId, entryId, passed, note) =>
    api.post(`/v1/dist/me/bug-reports/${reportId}/entries/${entryId}/verify`,
      { passed, note }),
  // v161.22.26: admin-only manual reply. Used from the dashboard, not the
  // tester PWA, but the helper lives here for completeness.
  adminReply: (id, { kind = 'status', body, sendPush = true }) =>
    api.post(`/v1/dist/admin/bug-reports/${id}/reply`, { kind, body, sendPush }),
};
